import React from 'react';
import './ExpenseItem.css';

export interface ExpenseItemProps {
  expense: {
    id: string;
    description: string;
    amount: number;
    paidBy: string;
    paidByName: string;
    date: string;
    splitType: 'equal' | 'percentage' | 'custom';
    participants: string[];
    category?: string;
  };
  currency?: string;
  onView?: (expenseId: string) => void;
  onEdit?: (expenseId: string) => void;
  onDelete?: (expenseId: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

const ExpenseItem: React.FC<ExpenseItemProps> = ({
  expense,
  currency = 'USD',
  onView,
  onEdit,
  onDelete,
  showActions = false,
  compact = false
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'accommodation': return '🏨';
      case 'food': return '🍽️';
      case 'transportation': return '🚗';
      case 'entertainment': return '🎉';
      case 'utilities': return '💡';
      case 'shopping': return '🛍️';
      case 'health': return '🏥';
      case 'education': return '📚';
      default: return '💰';
    }
  };

  const getSplitTypeLabel = (splitType: string) => {
    switch (splitType) {
      case 'equal': return 'Equal split';
      case 'percentage': return 'Percentage split';
      case 'custom': return 'Custom split';
      default: return 'Split';
    }
  };

  return (
    <div className={`expense-item ${compact ? 'compact' : ''}`}>
      <div className="expense-icon">
        {getCategoryIcon(expense.category)}
      </div>
      
      <div className="expense-details">
        <h4 className="expense-description">{expense.description}</h4>
        
        <div className="expense-meta">
          <span className="expense-payer">
            Paid by {expense.paidByName}
          </span>
          <span className="expense-separator">•</span>
          <span className="expense-date">
            {formatDate(expense.date)}
          </span>
        </div>
        
        <div className="expense-info">
          <span className="expense-split-type">
            {getSplitTypeLabel(expense.splitType)}
          </span>
          <span className="expense-separator">•</span>
          <span className="expense-participants">
            {expense.participants.length} people
          </span>
        </div>
      </div>
      
      <div className="expense-amount">
        {formatCurrency(expense.amount)}
      </div>
      
      {showActions && (
        <div className="expense-actions">
          {onView && (
            <button
              className="action-btn action-btn-ghost"
              onClick={() => onView(expense.id)}
              title="View details"
            >
              View
            </button>
          )}
          
          {onEdit && (
            <button
              className="action-btn action-btn-secondary"
              onClick={() => onEdit(expense.id)}
              title="Edit expense"
            >
              Edit
            </button>
          )}
          
          {onDelete && (
            <button
              className="action-btn action-btn-danger"
              onClick={() => onDelete(expense.id)}
              title="Delete expense"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseItem; 